import ResponsiveTable, { ResponsiveCell } from '../shared/ResponsiveTable';
import '../financials/financialsResponsive.css';
import ResponsiveRow from '../shared/ResponsiveRow';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import useModuleAccess from '../../hooks/useModuleAccess';

function ExpenseOnlyView({ transactions, chartOfAccounts, onAddTransaction, onEditTransaction, onDeleteTransaction, isUser = false }) {
    const { allowed: financialEditAllowed } = useModuleAccess(isUser ? 'financial' : null, 'edit');
    const canEdit = !isUser || financialEditAllowed;
    const [filterCategory, setFilterCategory] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedTransactionId, setExpandedTransactionId] = useState(null);
     // Pre-filter for expenses only
    const expenseTransactions = transactions.filter(t => t.type === 'expense');
     const filteredTransactions = expenseTransactions.filter(t => {
        const matchesCategory = filterCategory === 'All' || t.category === filterCategory;
        const matchesSearch = !searchTerm || 
            (t.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (t.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (t.merchant || '').toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });
     const categories = ['All', ...new Set(chartOfAccounts.filter(c => c.type === 'expense').map(c => c.name))];
     const totalExpenses = expenseTransactions.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
     return (
        <div className="ia-financials">
            <ResponsiveRow className="ia-financial-header flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Expense Transactions</h2>
                    <p className="text-sm text-gray-600 mt-1">Track all business expenses</p>
                    <div className="ia-financial-total mt-2 text-lg font-semibold text-red-600">
                        Total Expenses: ${totalExpenses.toLocaleString('en-US', {minimumFractionDigits: 2})}
                    </div>
                </div>
                {canEdit && <button onClick={onAddTransaction} className="bg-[var(--btn-fill)] text-[var(--btn-ink)] px-6 py-3 rounded-lg font-medium hover:bg-[var(--btn-fill-hover)] transition flex items-center gap-2">
                    <span>➕</span> Add Expense
                </button>}
            </ResponsiveRow>
             {/* Filters */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                <ResponsiveRow className="ia-financial-filters flex gap-4">
                    <input type="text" placeholder="Search expense transactions..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
                    <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
                        {categories.map(cat => <option key={cat}>{cat}</option>)}
                    </select>
                </ResponsiveRow>
            </div>
             {/* Expense Transactions Table */}
            <div className="bg-white rounded-lg shadow-md ia-financial-records overflow-hidden">
                <ResponsiveTable className="w-full">
                    <thead className="bg-red-50 border-b">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                            {canEdit && <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {filteredTransactions.length === 0 && (
                            <tr>
                                <td colSpan={canEdit ? 5 : 4} className="px-6 py-12 text-center">
                                    <div className="text-gray-500">
                                        <div className="text-6xl mb-4">📉</div>
                                        <h3 className="text-xl font-semibold mb-2">No expense transactions yet</h3>
                                        <p className="mb-4">Add your first expense to start tracking costs</p>
                                        {canEdit && <button onClick={onAddTransaction} className="bg-[var(--btn-fill)] text-[var(--btn-ink)] px-6 py-2 rounded-lg font-medium hover:bg-[var(--btn-fill-hover)] transition">
                                            Add Expense
                                        </button>}
                                    </div>
                                </td>
                            </tr>
                        )}
                        {filteredTransactions.map(transaction => (
                            <React.Fragment key={transaction.id}>
                                <tr 
                                    className="hover:bg-red-50 cursor-pointer transition"
                                    onClick={() => setExpandedTransactionId(expandedTransactionId === transaction.id ? null : transaction.id)}
                                >
                                    <ResponsiveCell label="Date" className="px-6 py-4 text-sm text-gray-900">{new Date(transaction.date).toLocaleDateString()}</ResponsiveCell>
                                    <ResponsiveCell label="Description" className="px-6 py-4">
                                        <div className="text-sm font-medium text-gray-900">{transaction.description}</div>
                                        {transaction.merchant && <div className="text-xs text-gray-500">{transaction.merchant}</div>}
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Category" className="px-6 py-4">
                                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                            {transaction.category}
                                        </span>
                                    </ResponsiveCell>
                                    {canEdit && <ResponsiveCell label="Amount" className="ia-financial-amount px-6 py-4 text-right">
                                        <span className="text-sm font-bold text-red-600">
                                            ${Math.abs(transaction.amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                                        </span>
                                    </ResponsiveCell>}
                                    <ResponsiveCell label="Actions" className="px-6 py-4 text-right">
                                        <button onClick={(e) => { e.stopPropagation(); onEditTransaction(transaction); }}
                                            className="text-blue-600 hover:text-blue-900 mr-3 text-sm font-medium">Edit</button>
                                        <button onClick={(e) => { e.stopPropagation(); onDeleteTransaction(transaction.id); }}
                                            className="text-red-600 hover:text-red-900 text-sm font-medium">Delete</button>
                                    </ResponsiveCell>
                                </tr>
                                {expandedTransactionId === transaction.id && transaction.memo && (
                                    <tr>
                                        <td colSpan={canEdit ? 5 : 4} className="px-6 py-3 bg-gray-50">
                                            <div className="text-sm text-gray-700">
                                                <strong>Memo:</strong> {transaction.memo}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </ResponsiveTable>
            </div>
        </div>
    );
}


export default ExpenseOnlyView;
